import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/i18n";
import { EASE } from "./Reveal";
import { DateTimePicker } from "./DateTimePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", phone: "", email: "", subject: "", description: "", preferred_datetime: "", consent: false, website: "" };

const Field = ({ id, label, hint, error, children }) => (
  <div className="group/field">
    <label htmlFor={id} className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-ink/70">
      <span>{label}</span>
      {hint && <span className="text-ink/40">{hint}</span>}
    </label>
    {children}
    {error && (
      <p role="alert" data-testid={`${id}-error`} className="mt-2 text-xs text-[#E8A0A0]">
        {error}
      </p>
    )}
  </div>
);

const inputCls = "field-input";

export const EnquiryForm = () => {
  const { t } = useContent();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = t.form.errors.fullName;
    if (!/^[+\d][\d\s\-()]{6,19}$/.test(form.phone.trim())) er.phone = t.form.errors.phone;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = t.form.errors.email;
    if (form.subject.trim().length < 2) er.subject = t.form.errors.subject;
    if (form.description.trim().length < 10) er.description = t.form.errors.description;
    if (!form.consent) er.consent = t.form.errors.consent;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, { ...form, preferred_datetime: form.preferred_datetime || null });
      setStatus("success");
      toast.success(t.form.successTitle, { description: t.form.success });
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.status === 429 ? t.form.errors.rateLimited : err?.response?.data?.detail || t.form.errors.generic;
      toast.error(typeof msg === "string" ? msg : t.form.errors.generic);
    }
  };

  return (
    <div className="relative border border-white/10 bg-navy-900/70 p-8 md:p-10" data-testid="enquiry-form-card">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="ok"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            role="status"
            data-testid="enquiry-success"
            className="flex min-h-[420px] flex-col justify-center"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold">
              <Check size={22} />
            </span>
            <h3 className="mt-8 font-serif text-3xl text-paper">{t.form.successTitle}</h3>
            <p className="mt-4 max-w-md text-base leading-relaxed text-ink">{t.form.success}</p>
            <button
              type="button"
              data-testid="enquiry-reset-button"
              onClick={() => {
                setForm(EMPTY);
                setStatus("idle");
              }}
              className="focus-ring mt-10 self-start font-mono text-[11px] uppercase tracking-[0.25em] text-gold underline-offset-4 hover:underline"
            >
              {t.form.another}
            </button>
          </motion.div>
        ) : (
          <motion.form key="form" noValidate onSubmit={submit} exit={{ opacity: 0 }} data-testid="enquiry-form" className="space-y-7">
            <h3 className="font-serif text-2xl text-paper sm:text-3xl">{t.form.title}</h3>
            <div className="grid gap-7 sm:grid-cols-2">
              <Field id="full_name" label={`${t.form.fullName} *`} error={errors.full_name}>
                <input id="full_name" data-testid="enquiry-input-name" className={inputCls} value={form.full_name} onChange={set("full_name")} autoComplete="name" required aria-invalid={!!errors.full_name} />
              </Field>
              <Field id="phone" label={`${t.form.phone} *`} error={errors.phone}>
                <input id="phone" type="tel" data-testid="enquiry-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" required aria-invalid={!!errors.phone} />
              </Field>
            </div>
            <div className="grid gap-7 sm:grid-cols-2">
              <Field id="email" label={t.form.email} hint={t.form.emailHint} error={errors.email}>
                <input id="email" type="email" data-testid="enquiry-input-email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" aria-invalid={!!errors.email} />
              </Field>
              <Field id="preferred_datetime" label={t.form.preferred} hint={t.form.preferredHint}>
                <DateTimePicker
                  id="preferred_datetime"
                  value={form.preferred_datetime}
                  onChange={(v) => setForm((f) => ({ ...f, preferred_datetime: v }))}
                />
              </Field>
            </div>
            <Field id="subject" label={`${t.form.subject} *`} error={errors.subject}>
              <input id="subject" data-testid="enquiry-input-subject" className={inputCls} value={form.subject} onChange={set("subject")} required aria-invalid={!!errors.subject} />
            </Field>
            <Field id="description" label={`${t.form.description} *`} error={errors.description}>
              <textarea id="description" rows={5} data-testid="enquiry-input-description" className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} required aria-invalid={!!errors.description} />
            </Field>
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>
            <div>
              <label className="flex cursor-pointer items-start gap-4 text-sm leading-relaxed text-ink">
                <input type="checkbox" data-testid="enquiry-consent-checkbox" checked={form.consent} onChange={set("consent")} className="peer sr-only" aria-invalid={!!errors.consent} />
                <span aria-hidden="true" className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-gold/60 transition-colors duration-300 peer-checked:bg-gold peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-gold-light [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100">
                  <Check size={14} className="text-navy-900 transition-opacity duration-200" />
                </span>
                <span>{t.form.consent}</span>
              </label>
              {errors.consent && (
                <p role="alert" data-testid="consent-error" className="mt-2 text-xs text-[#E8A0A0]">
                  {errors.consent}
                </p>
              )}
            </div>
            <button type="submit" disabled={status === "sending"} data-testid="enquiry-submit-button" className="btn-solid group w-full sm:w-auto">
              <span>{status === "sending" ? t.form.submitting : t.form.submit}</span>
              <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
